import { AfterViewInit, ElementRef, OnDestroy, ChangeDetectorRef, Component } from '@angular/core';
import Highcharts from 'highcharts';
import { FacilitiesService, Facility } from '../../core/facilities/facilities.service';
import { AbstractBaseComponent } from '../../shared/abstract-base.component';
import { DateRange } from '../../shared/date/date-range';
import { ChartOptionsFactory } from '../chart-options-factory';
import { takeUntil, catchError } from 'rxjs/operators';
import { SsiSummaryChartComponent } from './medication-summary/ssi/ssi-summary-chart.component';

/**
 * Base class for all summary components.
 */
@Component({
    template: '',
    host: {
        'class' : 'abstract-summary'
      }    
  })
export abstract class AbstractSummaryComponent extends AbstractBaseComponent implements AfterViewInit, OnDestroy {

    // Source data needed for rendering
    public dateRange: DateRange;
    public facilities: Facility[];


    /** Track created charts in order to destroy */
    protected charts: Highcharts.Chart[] = [];

    /** Construct */
    constructor(public facilitiesSvc: FacilitiesService) {
        super();
    }

    /**
     * Monitor facility selection changes.
     */
    ngAfterViewInit() {
        setTimeout(()=> {
        this.facilitiesSvc.selectedFacilities$
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (facilities: Facility[]) => {
                    this.facilities = facilities;
                    this.updateIfReady();
                });
            });
      
    }

    /**
     * Cleanup.
     */
    ngOnDestroy() {
        this.destroyAllCharts();
        super.ngOnDestroy();
    }

    /**
     * Destroy all charts.
     * (Child class may override and extend.)
     */
    destroyAllCharts(): void {
        this.charts.forEach(chart => chart.destroy());
        this.charts = [];
    }

    /**
     * Build all charts.
     * (Child class may override and extend.)
     */
    abstract buildAllCharts(): void;

    /**
     * Hook for child class to fetch and populate data on the display.
     * It should watch the stop$ stream for cancellations.
     */
    abstract fetchAndPopulate(): void;

    /**
     * Clear content of a chart - ready to be repopulated with data.
     * Used when refreshing the number of data-points of a single chart.
     * Otherwise, use destroyAllCharts() and buildAllCharts().
     */
    protected clearChart(chart: Highcharts.Chart): void {
        // Remove all series
        for (let idx = chart.series.length - 1; idx >= 0; --idx)
            chart.series[idx].remove(false);

        // Remove x-axis categories
        if (chart.xAxis.length)
            chart.xAxis[0].setCategories([], false);

        // Change subtitle to indicate loading state...
        this.setSubtitle(chart, '', false);
        chart.showLoading();
    }

    /**
     * After populating a chart, call this to make it adjust to the new data.
     * @param chart
     */
    protected redrawChart(chart: Highcharts.Chart): void {
        // Doesn't just redraw, but adjusts sizing as well.
        chart.setSize(null, null, true);
    }

    /**
     * Construct a Chart from options, and assign it to a div in the template.
     * Also tracks the Chart object for automatic clearing, redrawing, and destruction.
     *
     * @param chartDivRef reference to the div where the chart is to be displayed.
     * @param options
     * @returns {ChartObject}
     */
    protected createChart(chartDivRef: ElementRef, options: Highcharts.Options): Highcharts.Chart {
        options.chart.renderTo = chartDivRef.nativeElement;
        let chart = new Highcharts.Chart(options);
        this.charts.push(chart);
        return chart;
    }

    /**
     * Set the chart subtitle text and center it within the plot.
     *
     * @param chart Chart object
     * @param subtitleText {string}
     * @param center {boolean}
     */
    protected setSubtitle(chart: Highcharts.Chart, subtitleText: string, center: boolean): void {
        let subtitle: Highcharts.SubtitleOptions
            = { text: subtitleText, style: ChartOptionsFactory.subtitleStyleNormal };

        //If subtitle has been centered, it must always be centered. No way to convince Highcharts to undo this. :-(
        let existingSubtitle = chart.options.subtitle;
        if (center || existingSubtitle.verticalAlign === 'middle' || existingSubtitle.verticalAlign === 'top') {
            let plotHeight = (chart as any).plotHeight;
            let plotTop = (chart as any).plotTop;
            let titleHeight = 25; //Hack for version 10 to center subtitle in donut properly
            
            if (plotHeight && plotTop && titleHeight)
                subtitle = { text: subtitleText, verticalAlign: 'top', y: plotTop + (plotHeight * 0.5) + (titleHeight) };
            else
                subtitle = { text: subtitleText, verticalAlign: 'middle', y: 0 };

            subtitle.style = chart.series.length ?
                ChartOptionsFactory.subtitleStyleWithData : ChartOptionsFactory.subtitleStyleNoData;
        }

        chart.setTitle(null, subtitle);
    }

    protected resetChart(chart: Highcharts.Chart, chartDivRef: ElementRef): Highcharts.Chart{
        let index = this.charts.indexOf(chart);
        this.charts.splice(index,1);
        chart = this.createChart(chartDivRef, chart.options);

        chart.showLoading();

        return chart;
    }

    /**
     * Drives the lifecycle of:
     * 1. Clear existing content.
     * 2. Fetch data from backend.
     * 3. Repopulate and draw content.
     *
     * Automatically called whenever the facility or date range selections change.
     */
    protected abstract updateIfReady();

    /**
     * Do we have everything we need to process new data?
     * Only called by child implementations of updateIfReady().
     *
     * @returns {boolean} if true, proceed with update.
     */
    protected isReadyToUpdate(needsDate:boolean=true): boolean {
        
        var facilityReady = this.facilities != null && this.facilities.length > 0;

        var result = needsDate ? facilityReady && (this.dateRange != null && this.dateRange.startDate != null && this.dateRange.endDate != null): facilityReady;
        
        return result;
    }
}
